/**
 * Abstract base class for all managers in the system
 */

import type { EventBus } from "./EventBus";
import type { EventHandler, IManager, Unsubscribe } from "./types";

/**
 * Abstract base class that all managers extend
 * Provides lifecycle management and event handling
 */
export abstract class BaseManager implements IManager {
  protected readonly eventBus: EventBus;
  private _isInitialized = false;
  private _isDisposed = false;
  private _isInitializing = false;

  /**
   * Create a new BaseManager
   * @param eventBus The event bus for manager communication
   */
  constructor(eventBus: EventBus) {
    if (!eventBus) {
      throw new Error("EventBus is required");
    }
    this.eventBus = eventBus;
  }

  /**
   * Check if the manager has been initialized
   */
  get isInitialized(): boolean {
    return this._isInitialized;
  }

  /**
   * Check if the manager has been disposed
   */
  get isDisposed(): boolean {
    return this._isDisposed;
  }

  /**
   * Check if the manager is currently initializing
   */
  get isInitializing(): boolean {
    return this._isInitializing;
  }

  /**
   * Initialize the manager
   * Can only be called once
   */
  async initialize(): Promise<void> {
    if (this._isInitialized) {
      throw new Error(`${this.constructor.name} is already initialized`);
    }

    if (this._isDisposed) {
      throw new Error(`${this.constructor.name} has been disposed`);
    }

    if (this._isInitializing) {
      throw new Error(`${this.constructor.name} is already initializing`);
    }

    this._isInitializing = true;

    try {
      // Call the implementation-specific initialization
      await this.onInitialize();

      this._isInitialized = true;
      this._isInitializing = false;

      // Emit initialization event
      this.emit("initialized", { manager: this.constructor.name });
    } catch (error) {
      this._isInitializing = false;

      // Emit error event
      this.emit("error", {
        error: error instanceof Error ? error : new Error(String(error)),
        manager: this.constructor.name,
      });

      throw error;
    }
  }

  /**
   * Dispose of the manager and clean up resources
   * Can be called multiple times safely
   */
  dispose(): void {
    if (this._isDisposed) return;

    try {
      // Call the implementation-specific disposal
      this.onDispose();

      // Emit disposal event before marking as disposed
      this.emit("disposed", { manager: this.constructor.name });

      this._isDisposed = true;
      this._isInitialized = false;
    } catch (error) {
      // Emit error event even during disposal
      this.emit("error", {
        error: error instanceof Error ? error : new Error(String(error)),
        manager: this.constructor.name,
      });

      // Still mark as disposed even if there was an error
      this._isDisposed = true;
      this._isInitialized = false;

      throw error;
    }
  }

  /**
   * Implementation-specific initialization
   * Must be implemented by subclasses
   */
  protected abstract onInitialize(): Promise<void>;

  /**
   * Implementation-specific disposal
   * Must be implemented by subclasses
   */
  protected abstract onDispose(): void;

  /**
   * Emit an event through the event bus
   * @param event The event name (will be prefixed with manager name)
   * @param data The data to send with the event
   */
  protected emit<T = unknown>(event: string, data: T): void {
    if (this._isDisposed) {
      console.warn(
        `Attempting to emit event from disposed manager: ${this.constructor.name}`,
      );
      return;
    }

    // Emit the event without namespace (for compatibility with tests and simpler API)
    this.eventBus.emit(event, data);

    // Also emit with namespace for more specific listening if needed
    const namespacedEvent = `${this.constructor.name}:${event}`;
    this.eventBus.emit(namespacedEvent, data);
  }

  /**
   * Subscribe to an event
   * @param event The event name
   * @param handler The handler function
   * @returns Function to unsubscribe
   */
  protected on<T = unknown>(
    event: string,
    handler: EventHandler<T>,
  ): Unsubscribe {
    if (this._isDisposed) {
      console.warn(
        `Attempting to subscribe to event in disposed manager: ${this.constructor.name}`,
      );
      return () => {}; // Return no-op unsubscribe
    }

    return this.eventBus.on(event, handler);
  }

  /**
   * Subscribe to an event once
   * @param event The event name
   * @param handler The handler function
   * @returns Function to unsubscribe
   */
  protected once<T = unknown>(
    event: string,
    handler: EventHandler<T>,
  ): Unsubscribe {
    if (this._isDisposed) {
      console.warn(
        `Attempting to subscribe once to event in disposed manager: ${this.constructor.name}`,
      );
      return () => {}; // Return no-op unsubscribe
    }

    return this.eventBus.once(event, handler);
  }

  /**
   * Ensure the manager is initialized before performing an operation
   * @throws Error if not initialized or disposed
   */
  protected ensureInitialized(): void {
    if (this._isDisposed) {
      throw new Error(`${this.constructor.name} has been disposed`);
    }

    if (!this._isInitialized) {
      throw new Error(`${this.constructor.name} is not initialized`);
    }
  }

  /**
   * Ensure the manager is not disposed before performing an operation
   * @throws Error if disposed
   */
  protected ensureNotDisposed(): void {
    if (this._isDisposed) {
      throw new Error(`${this.constructor.name} has been disposed`);
    }
  }
}
